/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Table, Alert, Empty, Tag, Button, Tooltip } from "antd";
import { TablePaginationConfig } from "antd/es/table";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
	useLazyGetServersQuery,
	usePostSecretClientTokenMutation,
	usePostServerMutation,
	usePutServerMutation,
} from "../../../redux/servers/serverAPI";
import {
	updateOpenDrawerServer,
	updateServer,
	updateOpenModalSecretClient,
	updateFormAction,
} from "../../../redux/servers/serverSlice";
import { updateTotpTokenIsValid } from "../../../redux/authentication/authenticationSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import validatePermission from "../../../utils/validatePermissions";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
import AuthenticationTOTP from "../../authentication/AuthenticationTOTP";
import ServerModalSecretClient from "./ServerModalSecretClient";
import ServersTableActions from "./ServersTableActions";
import ServerSmtpModal from "./ServerSmtpModal";
import FormularyHeaderInputText from "../../components/formularyHeader/FormularyHeaderInputText";
import FormularyHeaderInputSelectOrganization from "../../components/formularyHeader/FormularyHeaderInputSelectOrganization";
import FormularyHeaderInputBoolean from "../../components/formularyHeader/FormularyHeaderInputBoolean";
import FormularyHeaderActionsCreate from "../../components/formularyHeader/FormularyHeaderActionsCreate";
import FormularyHeaderActionsEdit from "../../components/formularyHeader/FormularyHeaderActionsEdit";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
// CONSTANTS
import { BLANK_SERVER } from "../../../constants/server";

const ServersTable = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const navigate = useNavigate();
	const { Column, ColumnGroup } = Table;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [servers, setServers] = useState<ServerInterface[]>([]);
	const [server, setServer] = useState<ServerInterface>({ ...BLANK_SERVER });
	const [secretClientToken, setSecretClientToken] = useState("");
	const [filterName, setFilterName] = useState("");
	const [filterIP, setFilterIP] = useState("");
	const [openSmtpModal, setOpenSmtpModal] = useState(false);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 0,
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const { totpTokenIsValid, totpToken } = useSelector((state: RootState) => state.authentication);
	const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
	const { serverRefreshTable, formAction, server: selectedServer } = useSelector((state: RootState) => state.server);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetServersTable, { data, isLoading, isError, isFetching }] = useLazyGetServersQuery();
	const [triggerGetSecretClientMutation, { data: dataSecretClientToken, isLoading: isLoadingSecretClientToken }] =
		usePostSecretClientTokenMutation();
	const [
		postServer,
		{
			isSuccess: isSuccessPostServer,
			isLoading: isLoadingPostServer,
			isError: isErrorPostServer,
			error: errorPostServer,
			reset: resetPostServer,
		},
	] = usePostServerMutation();

	const [
		putServer,
		{
			isSuccess: isSuccessPutServer,
			isError: isErrorPutServer,
			error: errorPutServer,
			isLoading: isLoadingPutServer,
			reset: resetPutServer,
		},
	] = usePutServerMutation();

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const postChannel = async () => {
		const token = await GETJwtToken();
		const BODY = {
			token,
			body: {
				fb_enabled: server.fb_enabled,
				banks: [],
				ip: server.ip,
				name: server.name,
				channels: [],
				org_id: server.org_id,
				smtp_host: server.smtp_host,
				smtp_port: server.smtp_port,
				smtp_user: server.smtp_user,
				smtp_pass: server.smtp_pass,
				smtp_from: server.smtp_from,
			},
		};
		postServer(BODY);
	};
	const putChannel = async () => {
		const token = await GETJwtToken();
		putServer({ token, body: server });
	};

	const onClickSave = async () => {
		if (formAction === "create") {
			postChannel();
		} else if (formAction === "edit") {
			putChannel();
		}
	};

	const onClickResetForm = () => {
		resetPostServer();
		resetPutServer();
		dispatch(updateFormAction("create"));
		setServer({ ...BLANK_SERVER });
	};

	const onClickNavigateToBanks = (record: ServerInterface) => {
		navigate({
			pathname: "/edge-management/banks",
			search: `?idServer=${record.id}`,
		});
	};

	const onClickNavigateToChannels = (record: ServerInterface) => {
		navigate({
			pathname: "/edge-management/channels",
			search: `?idServer=${record.id}`,
		});
	};

	const getData = async (page: number, limit: number) => {
		let idOrganization = globalOrganizationId;
		if (userLogged && userLogged.org_id && userLogged.org_id === idOrganization) {
			idOrganization = "";
		}
		const searchAndFilterString = buildFilterString({
			filterName,
			filterIP,
			filterOrganizationName: idOrganization,
		});
		const token = await GETJwtToken();
		triggerGetServersTable({
			page,
			limit,
			token,
			searchAndFilterString,
		}).unwrap();
	};

	const onClickOpenServerInformation = (record: ServerInterface) => {
		if (record) {
			dispatch(updateServer(record));
			dispatch(updateOpenDrawerServer(true));
		}
	};

	const handleTableChange = (newValue: TablePaginationConfig) => {
		setPagination({
			...pagination,
			current: newValue.current,
			pageSize: newValue.pageSize,
			total: newValue.total,
		});
		getData(newValue.current || 1, newValue.pageSize || 10);
	};

	const TotpTokenIsValid = async () => {
		if (totpTokenIsValid) {
			const token = await GETJwtToken();
			triggerGetSecretClientMutation({
				token,
				body: {
					server_id: server.id,
					totpToken,
				},
			});
		}
	};

	const refreshTable = async () => {
		setPagination({
			...pagination,
			current: 1,
			pageSize: 10,
		});
		getData(1, 10);
	};

	const onChangeFormValue = (newValue: string | number, variableName: string) => {
		const copy: ServerInterface = structuredClone(server);
		if (variableName === "name" && typeof newValue === "string") {
			copy.name = newValue;
		} else if (variableName === "ip" && typeof newValue === "string") {
			copy.ip = newValue;
		} else if (variableName === "org_id" && typeof newValue === "string") {
			copy.org_id = newValue;
		}
		setServer(copy);
	};

	const onChangeSmtpValue = (newValue: string, variableName: string) => {
		const copy: ServerInterface = structuredClone(server);
		if (variableName === "smtp_host") {
			copy.smtp_host = newValue;
		} else if (variableName === "smtp_port") {
			copy.smtp_port = newValue;
		} else if (variableName === "smtp_user") {
			copy.smtp_user = newValue;
		} else if (variableName === "smtp_pass") {
			copy.smtp_pass = newValue;
		} else if (variableName === "smtp_from") {
			copy.smtp_from = newValue;
		}
		setServer(copy);
	};

	const onChangeFormValueBoolean = (newValue: boolean, variableName: string) => {
		const copy: ServerInterface = structuredClone(server);
		if (variableName === "fb_enabled") {
			copy.fb_enabled = newValue;
		} else if (variableName === "active") {
			copy.active = newValue;
		}
		setServer(copy);
	};

	const checkFormIsValid = () => {
		if (server.name === "" || server.ip === "" || server.org_id === "") {
			return true;
		}
		return false;
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (data && data.data) {
			setPagination({
				...pagination,
				total: data.total,
			});
			setServers(data.data);
		}
	}, [data]);

	useEffect(() => {
		if (isError) {
			setServers([]);
		}
	}, [isError]);

	useEffect(() => {
		if (serverRefreshTable) {
			getData(pagination.current || 1, pagination.pageSize || 10);
		}
	}, [serverRefreshTable]);

	useEffect(() => {
		if (totpTokenIsValid) {
			TotpTokenIsValid();
			setTimeout(() => {
				dispatch(updateTotpTokenIsValid(false));
			}, 1000);
		}
	}, [totpTokenIsValid]);

	useEffect(() => {
		if (dataSecretClientToken && dataSecretClientToken.data && dataSecretClientToken.data.Secret) {
			setSecretClientToken(dataSecretClientToken.data.Secret);
			dispatch(updateOpenModalSecretClient(true));
		}
	}, [dataSecretClientToken]);

	useEffect(() => {
		if (globalOrganizationId !== "") {
			refreshTable();
		}
	}, [globalOrganizationId]);

	useEffect(() => {
		if (selectedServer && selectedServer.id && selectedServer.id !== "") {
			setServer({ ...selectedServer });
		}
	}, [selectedServer]);

	useEffect(() => {
		if (isSuccessPostServer || isSuccessPutServer || isErrorPostServer || isErrorPutServer) {
			onClickResetForm();
		}
	}, [isSuccessPostServer, isSuccessPutServer, isErrorPostServer, isErrorPutServer]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='mt-3'>
			{validatePermission("servers-view", permissions) ? (
				<>
					{!isError ? (
						<Table
							rowClassName={(record, index) => getTableRowClass(index, theme)}
							dataSource={servers}
							loading={
								isLoading || isFetching || isLoadingSecretClientToken || isLoadingPostServer || isLoadingPutServer
							}
							className='my-custom-table'
							rowKey={(record) => record.id}
							size='small'
							pagination={pagination}
							onChange={handleTableChange}
							scroll={{ x: 900 }}
							footer={() => (
								<div className='generalStyles__flexEnd'>
									<Tag>{pagination.total} Rows</Tag>
								</div>
							)}
						>
							{/** NAME */}
							{FormularyHeaderInputText({
								columnName: t("general.name"),
								inputPlaceholder: t("general.name"),
								formAction,
								value: server.name,
								onChangeFormValue,
								variableName: "name",
								itemId: server.id,
								enabledCellClick: true,
								onClickCellName: onClickNavigateToBanks,
								enabledFilter: true,
								filterValue: filterName,
								setFilterValue: setFilterName,
								refreshTable,
								textLink: true,
							})}
							{/** IP */}
							{FormularyHeaderInputText({
								columnName: t("edgeManagement.servers.ip"),
								inputPlaceholder: t("edgeManagement.servers.ip"),
								formAction,
								value: server.ip,
								onChangeFormValue,
								variableName: "ip",
								itemId: server.id,
								enabledFilter: true,
								filterValue: filterIP,
								setFilterValue: setFilterIP,
								refreshTable,
								onClickCellName: onClickOpenServerInformation,
								enabledCellClick: true,
								inputValidationType: "ip",
							})}
							{/** ORGANIZATION */}
							{FormularyHeaderInputSelectOrganization({
								formAction,
								value: server.org_id,
								onChangeFormValue,
								variableName: "org_id",
								itemId: server.id,
								onClickCellName: onClickOpenServerInformation,
								enabledCellClick: true,
							})}
							{/** FLAT BUFFER ENABLE */}
							{FormularyHeaderInputBoolean({
								columnName: "Flat Buffer",
								inputPlaceholder: "Flat Buffer",
								formAction,
								value: server.fb_enabled,
								onChangeFormValueBoolean,
								variableName: "fb_enabled",
								itemId: server.id,
								onClickCellName: onClickOpenServerInformation,
								enabledCellClick: true,
							})}
							{/** STATUS */}
							{FormularyHeaderInputBoolean({
								columnName: t("general.active"),
								inputPlaceholder: t("general.active"),
								formAction,
								value: server.active,
								onChangeFormValueBoolean,
								variableName: "active",
								itemId: server.id,
								onClickCellName: onClickOpenServerInformation,
								enabledCellClick: true,
							})}
							{/** SMTP CONFIGURATION */}
							<ColumnGroup title='SMTP'>
								<Column
									width='120px'
									title={
										<div>
											{formAction === "create" || formAction === "edit" ? (
												<Tooltip title='SMTP Configuration'>
													<Button
														size='small'
														style={{ width: "100%" }}
														onClick={() => setOpenSmtpModal(true)}
													>
														SMTP
													</Button>
												</Tooltip>
											) : (
												<span>SMTP</span>
											)}
										</div>
									}
									key='smtp'
									render={(_: any, record: ServerInterface) => (
										<div>
											{formAction === "edit" && record.id === server.id ? (
												<Tooltip title='SMTP Configuration'>
													<Button
														size='small'
														style={{ width: "100%" }}
														onClick={() => setOpenSmtpModal(true)}
													>
														SMTP
													</Button>
												</Tooltip>
											) : (
												<Tooltip
													title={
														record.smtp_host
															? `${record.smtp_host}:${record.smtp_port}`
															: t("general.notConfigured") || "Not configured"
													}
												>
													<Tag
														color={record.smtp_host ? "green" : "default"}
														style={{ width: "100%", textAlign: "center" }}
													>
														{record.smtp_host ? "ON" : "OFF"}
													</Tag>
												</Tooltip>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** TOTAL CHANNELS ASSIGNED */}
							<Column
								onCell={(record: ServerInterface) => ({
									onClick: () => {
										onClickNavigateToChannels(record);
									},
								})}
								width='150px'
								className='generalStyles__tableHeaderLink'
								title={t("edgeManagement.channels.channels")}
								dataIndex='channels'
								key='channels'
								render={(text, record: ServerInterface) => (
									<>
										<div>{record && record.channels ? record.channels.length : 0}</div>
									</>
								)}
							/>
							{/** ACTIONS */}
							<ColumnGroup align='center' title={t("edgeManagement.banks.actions")}>
								<Column
									align='center'
									title={
										<FormularyHeaderActionsCreate
											formAction={formAction}
											formIsValid={checkFormIsValid()}
											onClickSave={onClickSave}
											onClickResetForm={onClickResetForm}
										/>
									}
									key='action'
									render={(_: any, record: ServerInterface) => (
										<div>
											{formAction === "edit" && record.id === server.id ? (
												<FormularyHeaderActionsEdit
													formIsValid={checkFormIsValid()}
													onClickSave={onClickSave}
													onClickResetForm={onClickResetForm}
												/>
											) : (
												<ServersTableActions selectedItem={record} />
											)}
										</div>
									)}
								/>
							</ColumnGroup>
						</Table>
					) : (
						<Alert
							message={t("organizationManagement.errorDescription.error")}
							description={t("organizationManagement.errorDescription.text1")}
							type='error'
							showIcon
							className='mt-3'
						/>
					)}
				</>
			) : (
				<>
					<div className='generalStyles__noAccessToListTable'>
						<Empty description={t("edgeManagement.servers.listNotAvailable")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
					</div>
				</>
			)}
			{/** ********************************************* */}
			{/** AUTHENTICATION TOPTP TOKEN */}
			<AuthenticationTOTP />
			{/** ********************************************* */}
			{/** SECRET CLIENT */}
			<ServerModalSecretClient secretClientToken={secretClientToken} />
			{/** ********************************************* */}
			{/** SMTP MODAL */}
			<ServerSmtpModal
				open={openSmtpModal}
				server={server}
				formAction={formAction}
				onChangeSmtpValue={onChangeSmtpValue}
				onClose={() => setOpenSmtpModal(false)}
			/>
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostServer}
				isSuccess={isSuccessPostServer}
				requestType='POST'
				error={errorPostServer}
				name='Server'
			/>
			<GlobalAlert2
				isError={isErrorPutServer}
				isSuccess={isSuccessPutServer}
				requestType='PUT'
				error={errorPutServer}
				name='Server'
			/>
		</div>
	);
};

export default ServersTable;
